import { calculateDistance as haversineDistance, isWithinZimbabwe as checkZimbabweBounds } from '../utils/geo';

/**
 * Google Places API Service (New)
 * Uses the Places API (New) — promise-based AutocompleteSuggestion & Place classes
 *
 * Required Google Cloud APIs:
 * 1. Maps JavaScript API
 * 2. Places API (New)
 * 3. Geocoding API
 *
 * Set REACT_APP_GOOGLE_MAPS_API_KEY in .env and call loadGoogleMaps() at startup (App.js)
 */

const GOOGLE_PLACES_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

// Wait for Google Maps JS SDK to load
const waitForGoogleMaps = (maxWait = 5000) => {
  return new Promise((resolve) => {
    if (window.google?.maps?.places) {
      resolve(true);
      return;
    }
    let attempts = 0;
    const maxAttempts = maxWait / 100;
    const interval = setInterval(() => {
      attempts++;
      if (window.google?.maps?.places) {
        clearInterval(interval);
        resolve(true);
      } else if (attempts >= maxAttempts) {
        clearInterval(interval);
        resolve(false);
      }
    }, 100);
  });
};

// Zimbabwe cities fallback data
const ZIMBABWE_CITIES = {
  'Harare': { name: 'Harare', coordinates: { lat: -17.8252, lng: 31.0335 }, province: 'Harare', country: 'Zimbabwe', type: 'city' },
  'Bulawayo': { name: 'Bulawayo', coordinates: { lat: -20.1619, lng: 28.5810 }, province: 'Bulawayo', country: 'Zimbabwe', type: 'city' },
  'Chitungwiza': { name: 'Chitungwiza', coordinates: { lat: -18.0086, lng: 31.0614 }, province: 'Harare', country: 'Zimbabwe', type: 'city' },
  'Mutare': { name: 'Mutare', coordinates: { lat: -18.9707, lng: 32.6473 }, province: 'Manicaland', country: 'Zimbabwe', type: 'city' },
  'Gweru': { name: 'Gweru', coordinates: { lat: -19.4500, lng: 29.8167 }, province: 'Midlands', country: 'Zimbabwe', type: 'city' },
  'Kwekwe': { name: 'Kwekwe', coordinates: { lat: -18.9167, lng: 29.8167 }, province: 'Midlands', country: 'Zimbabwe', type: 'city' },
  'Kadoma': { name: 'Kadoma', coordinates: { lat: -18.3333, lng: 29.9167 }, province: 'Mashonaland West', country: 'Zimbabwe', type: 'city' },
  'Masvingo': { name: 'Masvingo', coordinates: { lat: -20.0638, lng: 30.8276 }, province: 'Masvingo', country: 'Zimbabwe', type: 'city' },
  'Chinhoyi': { name: 'Chinhoyi', coordinates: { lat: -17.3667, lng: 30.2000 }, province: 'Mashonaland West', country: 'Zimbabwe', type: 'city' },
  'Marondera': { name: 'Marondera', coordinates: { lat: -18.1851, lng: 31.5511 }, province: 'Mashonaland East', country: 'Zimbabwe', type: 'city' }
};

const POPULAR_AREAS = {
  'Harare': [
    'Borrowdale', 'Avondale', 'Mount Pleasant', 'Highlands', 'Ballantyne Park',
    'Greendale', 'Marlborough', 'Newlands', 'Chisipite', 'Glen View',
    'Westgate', 'Belgravia', 'Alexandra Park', 'Greystone Park', 'Eastlea'
  ],
  'Bulawayo': [
    'Suburbs', 'Hillside', 'Burnside', 'Riverside', 'Kumalo', 'North End',
    'Belmont', 'Selborne Park', 'Newton West', 'Parklands'
  ]
};

const delay = (ms = 300) => new Promise(resolve => setTimeout(resolve, ms));

class PlacesService {
  constructor() {
    this.geocoderService = null;
    this.placesReady = false;
    this.initialized = false;
  }

  async initialize() {
    if (this.initialized) return this.placesReady;

    try {
      const googleMapsLoaded = await waitForGoogleMaps();

      if (GOOGLE_PLACES_API_KEY && googleMapsLoaded && window.google?.maps?.places) {
        // New Places API uses static methods — no service instance needed for autocomplete
        // Geocoder is still the classic class
        this.geocoderService = new window.google.maps.Geocoder();
        this.placesReady = true;
        this.initialized = true;
        console.log('Google Places API (New) initialized successfully');
        return true;
      } else {
        if (!GOOGLE_PLACES_API_KEY) {
          console.warn('Google Places API key not found. Using fallback location data.');
        } else if (!googleMapsLoaded) {
          console.warn('Google Maps script not loaded. Using fallback location data.');
        }
        this.initialized = true;
        return false;
      }
    } catch (error) {
      console.error('Failed to initialize Google Places API:', error);
      this.initialized = true;
      return false;
    }
  }

  // ─── Autocomplete (New Places API via REST) ────────────────────────────

  async getAddressPredictions(input, options = {}) {
    await this.initialize();

    try {
      if (GOOGLE_PLACES_API_KEY) {
        // Map old 'types' to new API 'includedPrimaryTypes'
        const { types, componentRestrictions } = options;
        const typeMap = {
          'address': 'street_address',
          'establishment': 'establishment',
          'geocode': 'geocode',
          'street_address': 'street_address',
          'route': 'route',
          '(cities)': 'locality',
          '(regions)': 'administrative_area_level_1',
        };

        const body = { input };
        if (types) {
          body.includedPrimaryTypes = types.map(t => typeMap[t] || t).filter(Boolean);
        }
        if (componentRestrictions?.country) {
          body.includedRegionCodes = [componentRestrictions.country];
        }

        const response = await fetch('https://places.googleapis.com/v1/places:autocomplete', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Goog-Api-Key': GOOGLE_PLACES_API_KEY,
          },
          body: JSON.stringify(body),
        });

        if (response.ok) {
          const data = await response.json();
          if (data.suggestions?.length > 0) {
            const results = data.suggestions
              .filter(s => s.placePrediction)
              .map(s => {
                const p = s.placePrediction;
                return {
                  id: p.placeId,
                  name: p.structuredFormat?.mainText?.text || p.text?.text || '',
                  address: p.text?.text || '',
                  type: 'google_place',
                  placeId: p.placeId,
                  types: p.types || []
                };
              });

            if (results.length > 0) {
              return { success: true, data: results, source: 'google_places' };
            }
          }
        } else {
          // Non-OK from the Places API (New) is almost always a Google Cloud
          // CONFIG problem, not a code bug: billing disabled on the project, or
          // the API key's restrictions exclude "Places API (New)". Surface it
          // ONCE (autocomplete fires on every keystroke) so the live-but-silent
          // fallback-to-Zimbabwe-cities degradation is debuggable. Fix is in the
          // Cloud console: enable billing + add Places API (New)/Geocoding API
          // to the key, keeping the *.eqaya.com referrer restriction.
          if (!this._placesErrorLogged) {
            this._placesErrorLogged = true;
            const detail = await response.text().catch(() => '');
            console.warn(
              `[placesService] Places API (New) returned HTTP ${response.status} — ` +
              'real address autocomplete is OFF, using Zimbabwe-city fallback. ' +
              'Check Google Cloud billing + API-key restrictions. Response: ' +
              detail.slice(0, 300)
            );
          }
        }

        // API returned no results — fall through to fallback
        return this.getFallbackPredictions(input);
      }

      return this.getFallbackPredictions(input);
    } catch (error) {
      console.error('Address predictions error:', error);
      return this.getFallbackPredictions(input);
    }
  }

  // ─── Place Details (New Places API via REST) ─────────────────────────

  async getPlaceDetails(placeId) {
    await this.initialize();

    try {
      if (GOOGLE_PLACES_API_KEY) {
        const response = await fetch(
          `https://places.googleapis.com/v1/places/${placeId}`,
          {
            headers: {
              'X-Goog-Api-Key': GOOGLE_PLACES_API_KEY,
              'X-Goog-FieldMask': 'displayName,formattedAddress,location,addressComponents,types',
            },
          }
        );

        if (response.ok) {
          const place = await response.json();
          const result = {
            id: placeId,
            name: place.displayName?.text || '',
            address: place.formattedAddress || '',
            coordinates: place.location ? {
              lat: place.location.latitude,
              lng: place.location.longitude
            } : null,
            addressComponents: this.parseNewAddressComponents(place.addressComponents),
            types: place.types || []
          };

          // Fallback: if Google didn't return a separate street_number component,
          // try extracting a leading number from the formatted address
          if (!result.addressComponents.streetNumber && place.formattedAddress) {
            const match = place.formattedAddress.match(/^(\d+)\s/);
            if (match) {
              result.addressComponents.streetNumber = match[1];
            }
          }

          return { success: true, data: result, source: 'google_places' };
        }
      }

      return this.getFallbackPlaceDetails(placeId);
    } catch (error) {
      console.error('Place details error:', error);
      return this.getFallbackPlaceDetails(placeId);
    }
  }

  // ─── Geocoding (unchanged — Geocoder is not part of Places API) ───────

  async geocodeAddress(address) {
    await this.initialize();
    await delay();

    try {
      if (this.geocoderService && window.google?.maps?.Geocoder) {
        return new Promise((resolve, reject) => {
          this.geocoderService.geocode(
            { address },
            (results, status) => {
              if (status === window.google.maps.GeocoderStatus.OK && results[0]) {
                const result = results[0];
                resolve({
                  success: true,
                  data: {
                    coordinates: {
                      lat: result.geometry.location.lat(),
                      lng: result.geometry.location.lng()
                    },
                    address: result.formatted_address,
                    addressComponents: this.parseAddressComponents(result.address_components),
                    accuracy: result.geometry.location_type
                  },
                  source: 'google_geocoding'
                });
              } else {
                reject(new Error(`Geocoding error: ${status}`));
              }
            }
          );
        });
      }
      return this.fallbackGeocode(address);
    } catch (error) {
      console.error('Geocoding error:', error);
      return this.fallbackGeocode(address);
    }
  }

  async reverseGeocode(lat, lng) {
    await this.initialize();
    await delay();

    try {
      if (this.geocoderService && window.google?.maps?.Geocoder) {
        return new Promise((resolve, reject) => {
          this.geocoderService.geocode(
            { location: { lat, lng } },
            (results, status) => {
              if (status === window.google.maps.GeocoderStatus.OK && results[0]) {
                const result = results[0];
                resolve({
                  success: true,
                  data: {
                    address: result.formatted_address,
                    addressComponents: this.parseAddressComponents(result.address_components),
                    placeId: result.place_id
                  },
                  source: 'google_geocoding'
                });
              } else {
                reject(new Error(`Reverse geocoding error: ${status}`));
              }
            }
          );
        });
      }
      return this.fallbackReverseGeocode(lat, lng);
    } catch (error) {
      console.error('Reverse geocoding error:', error);
      return this.fallbackReverseGeocode(lat, lng);
    }
  }

  // ─── Fallback Methods ─────────────────────────────────────────────────

  async getFallbackPredictions(input) {
    await delay();
    const lowerInput = input.toLowerCase();
    const results = [];

    Object.values(ZIMBABWE_CITIES).forEach(city => {
      if (city.name.toLowerCase().includes(lowerInput)) {
        results.push({
          id: `city_${city.name}`,
          name: city.name,
          address: `${city.name}, ${city.province}, Zimbabwe`,
          type: 'city',
          coordinates: city.coordinates,
          province: city.province,
          country: city.country
        });
      }
    });

    Object.entries(POPULAR_AREAS).forEach(([city, areas]) => {
      areas.forEach(area => {
        if (area.toLowerCase().includes(lowerInput)) {
          const cityData = ZIMBABWE_CITIES[city];
          results.push({
            id: `area_${area}_${city}`,
            name: area,
            address: `${area}, ${city}, Zimbabwe`,
            type: 'suburb',
            coordinates: cityData?.coordinates,
            city,
            province: cityData?.province,
            country: 'Zimbabwe'
          });
        }
      });
    });

    if (results.length === 0 && input.length > 2) {
      results.push({
        id: `custom_${Date.now()}`,
        name: input,
        address: `${input}, Zimbabwe`,
        type: 'custom',
        coordinates: null,
        country: 'Zimbabwe'
      });
    }

    return { success: true, data: results.slice(0, 10), source: 'fallback' };
  }

  async getFallbackPlaceDetails(locationId) {
    await delay();

    if (locationId.startsWith('city_')) {
      const cityName = locationId.replace('city_', '');
      const cityData = ZIMBABWE_CITIES[cityName];

      if (cityData) {
        return {
          success: true,
          data: {
            id: locationId,
            name: cityData.name,
            address: `${cityData.name}, ${cityData.province}, Zimbabwe`,
            coordinates: cityData.coordinates,
            addressComponents: { city: cityData.name, province: cityData.province, country: cityData.country },
            types: ['locality']
          },
          source: 'fallback'
        };
      }
    }

    return { success: false, error: 'Location details not found', source: 'fallback' };
  }

  async fallbackGeocode(address) {
    await delay();
    const lowerAddress = address.toLowerCase();

    for (const city of Object.values(ZIMBABWE_CITIES)) {
      if (lowerAddress.includes(city.name.toLowerCase())) {
        return {
          success: true,
          data: {
            coordinates: city.coordinates,
            address: `${city.name}, ${city.province}, Zimbabwe`,
            addressComponents: { city: city.name, province: city.province, country: city.country },
            accuracy: 'APPROXIMATE'
          },
          source: 'fallback'
        };
      }
    }

    return { success: false, error: 'Could not geocode address', source: 'fallback' };
  }

  async fallbackReverseGeocode(lat, lng) {
    await delay();
    let nearestCity = null;
    let shortestDistance = Infinity;

    Object.values(ZIMBABWE_CITIES).forEach(city => {
      const distance = this.calculateDistance(lat, lng, city.coordinates.lat, city.coordinates.lng);
      if (distance < shortestDistance) {
        shortestDistance = distance;
        nearestCity = city;
      }
    });

    if (nearestCity && shortestDistance < 50) {
      return {
        success: true,
        data: {
          address: `Near ${nearestCity.name}, ${nearestCity.province}, Zimbabwe`,
          addressComponents: { city: nearestCity.name, province: nearestCity.province, country: nearestCity.country },
          nearestCity: { name: nearestCity.name, distance: Math.round(shortestDistance) }
        },
        source: 'fallback'
      };
    }

    return { success: false, error: 'Could not reverse geocode coordinates', source: 'fallback' };
  }

  // ─── Address Component Parsers ────────────────────────────────────────

  // Parse New Places API addressComponents (array of AddressComponent objects)
  parseNewAddressComponents(components) {
    const parsed = {
      streetNumber: '', route: '', locality: '', sublocality: '',
      administrativeAreaLevel1: '', administrativeAreaLevel2: '',
      country: '', postalCode: ''
    };

    if (!components) return parsed;

    components.forEach(component => {
      const types = component.types || [];
      const value = component.longText || component.long_name || '';

      if (types.includes('street_number')) parsed.streetNumber = value;
      else if (types.includes('route')) parsed.route = value;
      else if (types.includes('locality')) parsed.locality = value;
      else if (types.includes('sublocality')) parsed.sublocality = value;
      else if (types.includes('administrative_area_level_1')) parsed.administrativeAreaLevel1 = value;
      else if (types.includes('administrative_area_level_2')) parsed.administrativeAreaLevel2 = value;
      else if (types.includes('country')) parsed.country = value;
      else if (types.includes('postal_code')) parsed.postalCode = value;
    });

    return parsed;
  }

  // Parse legacy Geocoder address_components (still callback-based)
  parseAddressComponents(components) {
    const parsed = {
      streetNumber: '', route: '', locality: '', sublocality: '',
      administrativeAreaLevel1: '', administrativeAreaLevel2: '',
      country: '', postalCode: ''
    };

    components?.forEach(component => {
      const type = component.types[0];
      switch (type) {
        case 'street_number': parsed.streetNumber = component.long_name; break;
        case 'route': parsed.route = component.long_name; break;
        case 'locality': parsed.locality = component.long_name; break;
        case 'sublocality': parsed.sublocality = component.long_name; break;
        case 'administrative_area_level_1': parsed.administrativeAreaLevel1 = component.long_name; break;
        case 'administrative_area_level_2': parsed.administrativeAreaLevel2 = component.long_name; break;
        case 'country': parsed.country = component.long_name; break;
        case 'postal_code': parsed.postalCode = component.long_name; break;
        default: break;
      }
    });

    return parsed;
  }

  // ─── Utilities ────────────────────────────────────────────────────────

  calculateDistance(lat1, lon1, lat2, lon2) {
    return haversineDistance(lat1, lon1, lat2, lon2);
  }

  isWithinZimbabwe(lat, lng) {
    return checkZimbabweBounds(lat, lng);
  }

  getStatus() {
    return {
      initialized: this.initialized,
      googlePlacesAvailable: this.placesReady,
      apiKey: GOOGLE_PLACES_API_KEY ? 'Set' : 'Not Set',
      fallbackMode: !this.placesReady,
      apiVersion: 'new'
    };
  }
}

const placesService = new PlacesService();

export default placesService;
